import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@apollo/client/react';

import { DepositStatusEnum, EscrowStatusEnum, type IClientChangesRequestedPayload } from 'src/graphql/schema';
import { ClientChangesRequestedDocument } from 'src/graphql/actions/suggestChanges/clientChangesRequested.query';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { cn } from 'src/lib/utils';

type ITab = {
  label: string;
  value: string;
  hasChildren?: boolean;
  showAlert?: (args: {
    status: EscrowStatusEnum;
    depositStatus: DepositStatusEnum;
    isApproved?: boolean;
    isRequestedChanges?: boolean;
  }) => 'primary' | 'warning' | undefined;
};

const tabs: ITab[] = [
  { label: 'Overview', value: 'overview' },
  {
    label: 'Agreement',
    value: 'agreement',
    showAlert: ({ status, isApproved = false, isRequestedChanges = false }) => {
      if (isRequestedChanges) return 'warning';
      if (status === EscrowStatusEnum.Onboarding && !isApproved) return 'primary';
      return undefined;
    },
  },
  {
    label: 'Deposits',
    value: 'deposits',
    showAlert: ({ status, depositStatus, isRequestedChanges = false }) => {
      if (isRequestedChanges) return 'warning';
      if (status === EscrowStatusEnum.Signed && depositStatus === DepositStatusEnum.NoConfiguration) return 'primary';
      return undefined;
    },
  },
  { label: 'Reports', value: 'reports' },
  { label: 'Members', value: 'members' },
  {
    label: 'Settings',
    value: 'settings',
    hasChildren: true,
    showAlert: ({ isRequestedChanges = false }) => {
      if (isRequestedChanges) return 'warning';
      return undefined;
    },
  },
];

const getActiveTab = (pathname: string) => {
  const pathSegments = pathname.split('/').filter(Boolean);

  const hasChildrenTab = tabs.find((tab) => tab.hasChildren && pathSegments.includes(tab.value));

  // Prefer a tab with hasChildren if its value is in the path
  if (hasChildrenTab) return hasChildrenTab.value;

  // Fallback to the last segment of the path
  return pathSegments[pathSegments.length - 1];
};

const AlertBadge = ({ type }: { type: 'primary' | 'warning' | undefined }) => {
  if (!type) return null;

  return <span className={cn('size-1.5 rounded-full bg-foreground', type === 'warning' && 'bg-orange-400')} />;
};

const hasRequestedChanges = (changes: IClientChangesRequestedPayload | undefined, tabValue: string) => {
  if (!changes) return false;

  if (tabValue === 'deposits') return Boolean(changes['deposits']);

  if (tabValue === 'settings')
    return [changes.beneficiary, changes.depositor, changes.notifications, changes.agreement].some(Boolean);
};

const NavigationTabs = () => {
  const {
    softwareEscrow,
    softwareEscrow: { status, depositStatus, agreement, app },
  } = useSoftwareEscrow();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: clientChangesRequested } = useQuery(ClientChangesRequestedDocument);

  const onValueChange = (value: string) => navigate(`/software-escrows/${softwareEscrow.id}/${value}`);

  const visibleTabs = app.owned ? tabs.filter((tab) => tab.value !== 'members') : tabs;

  return (
    <Tabs onValueChange={onValueChange} value={getActiveTab(pathname)} className="overflow-x-auto">
      <TabsList className="justify-start p-0 h-9 md:h-12">
        {visibleTabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            className="bg-white rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:shadow-none h-full px-3 transition-all duration-300 font-semibold"
          >
            <span className="block">{tab.label}</span>
            <AlertBadge
              type={tab.showAlert?.({
                status,
                depositStatus,
                isApproved: agreement?.approved,
                isRequestedChanges: hasRequestedChanges(clientChangesRequested?.clientChangesRequested, tab.value),
              })}
            />
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};

export default NavigationTabs;
